import {
  faArrowUpRightFromSquare,
  faFacebook,
  faGlobe,
  faInstagram,
  faTiktok,
  faXTwitter,
  faYoutube,
} from '@/components/icon/FaIcon/FaIcon';
import type { MaterialCategoryType } from '@/constants';
import type { BadgeStatusType, Creator, Magazine, SaleStatus } from '@/types';

type SnsLink = {
  id: number;
  type: SnsTypeType;
  label: string;
  link: string;
  displayOrder: number;
};

export type SnsGroup = {
  label: string;
  displayOrder: number;
  links: SnsLink[];
};

export type GfWorkDetail = {
  id: number;
  name: string;
  nameKana: string;
  nameEn: string;
  description: string;
  imagePath: string;
  logoPath: string | null;
  thumbnailPath: string;
  ogpImagePath: string | null;
  ogpDescription: string | null;
  startDate: string;
  endDate: string;
  startIssueNumber: number | null;
  endIssueNumber: number | null;
  janCode: string;
  publisherCode: string;
  url: string;
  source: string;
  color: string;
  displayOrder: number;
  badgeStatus: BadgeStatusType | null;
  creators: Creator[];
  magazines: Magazine[];
  sns: SnsGroup[];
  createdAt: string;
  updatedAt: string;
};

export type GfMaterialDetail = {
  id: number;
  materialCode: string;
  categoryName: MaterialCategoryType;
  name: string;
  nameKana: string;
  description: string;
  imagePath: string;
  thumbnailPath: string;
  width: number; // 幅（350DPI換算）
  height: number; // 高さ（350DPI換算）
  originalWidth: number; // 元の幅（素材のDPI）
  originalHeight: number; // 元の高さ（素材のDPI）
  dpi: number;
  constraints: {
    minScale: number;
    maxScale: number;
    canRotate: boolean;
    isSpecialLogo: boolean;
  };
  additionalTaxExcludedPrice: number;
  displayOrder: number;
  badgeStatus: BadgeStatusType | null;
  tags: {
    id: number;
    name: string;
  }[];
  availableProducts: {
    id: number;
    name: string;
    saleStatus: SaleStatus;
    thumbnailPaths: {
      main: string;
      sub: string;
    };
    basePrice: number;
    categories?: {
      id: number;
      name: string;
      displayOrder: number;
    }[];
  }[];
  work: {
    id: number;
    name: string;
    creators: {
      id: number;
      name: string;
    }[];
    /** ロゴ URL。未設定時は空文字が返る */
    logoUrl: string;
    copyrightJa: string | null;
    copyrightEn: string | null;
    copyrightShort: string | null;
    startDate: string | null;
    endDate: string | null;
  };
  createdAt: string;
  updatedAt: string;
  isFavorite: boolean;
};

export type GfMaterialItem = {
  id: number;
  materialCode: string;
  materialType: MaterialCategoryType;
  name: string;
  nameKana: string;
  description: string;
  thumbnailPath: string;
  dpi: number;
  additionalTaxExcludedPrice: number;
  displayOrder: number;
  badgeStatus?: BadgeStatusType | '';
  tags: {
    id: number;
    name: string;
  }[];
  isFavorite: boolean;
  work?: {
    id?: number;
    name?: string;
    color?: string;
  };
  createdAt: string;
  updatedAt: string;
};

export type GfMaterialCategoryItem = {
  materials: GfMaterialItem[];
  categoryName: MaterialCategoryType | 'all';
  totalCount: number;
  hasMore: boolean;
};

// レスポンスの形式
export type GfMaterialList = {
  categories: GfMaterialCategoryItem[];
  totalCount: number;
};

export type GfMaterialPopularBase = {
  id: number;
  materialCode: string;
  categoryName: MaterialCategoryType;
  name: string;
  nameKana: string;
  description: string;
  thumbnailPath: string;
  dpi: number;
  additionalTaxExcludedPrice: number;
  additionalTaxIncludedPrice: number;
  displayOrder: number;
  badgeStatus?: BadgeStatusType | '';
  tags: {
    id: number;
    name: string;
  }[];
  workId: number;
  workName: string;
  workColor: string;
  popularityScore: number;
  purchaseCount: number;
  uniqueUsers: number;
  createdAt: string;
  updatedAt: string;
  isFavorite: boolean;
};

export type GfMaterialPopularList = GfMaterialPopularBase[];

/** ItemFilterModalに渡すアイテム。materialCountを指定した画面のみ件数表示・0件時の非活性化を行う */
export type ItemFilterProduct = {
  id: number;
  name: string;
  iconImageUrl: string;
  /**
   * 対象素材数。指定した場合のみ「(件数)」表示と0件時の非活性化を行う。
   * 未指定の画面（例: みんなのデザイン）では件数表示・非活性化を行わない従来の挙動になる。
   */
  materialCount?: number;
};

//作品一覧のビューモードの定義
export const ViewMode = {
  tile: { value: 'tile', text: 'リスト表示に切替' },
  list: { value: 'list', text: 'タイル表示に切替' },
} as const;
export type ViewModeType = keyof typeof ViewMode;

//作品一覧の並び替えプルダウン定義
//TODO: API側のソートはcreated_at_descとcreated_at_ascしかない。仕様が決まり次第sortの値を変更する
// export const SortOptions = {
//   newOrder: { value: 'newOrder', text: '新着順', sortKey: 'created_at_desc' },
//   titleOrder: { value: 'titleOrder', text: '50音順', sortKey: 'created_at_asc' },
//   publishedDateOrder: { value: 'publishedDateOrder', text: '掲載開始年月順', sortKey: 'created_at_asc' },
// } as const;

//TODO:ソート項目を仮で追加日時順のみにする
export const SortOptions = {
  createdOrderDesc: { value: 'createdOrderDesc', text: '追加日時(降順)', sortKey: 'created_at_desc' },
  createdOrderAsc: { value: 'createdOrderAsc', text: '追加日時(昇順)', sortKey: 'created_at_asc' },
} as const;
export type SortOptionsType = keyof typeof SortOptions;

export const SnsType = {
  x: { value: 'x', text: 'X', icon: faXTwitter },
  instagram: { value: 'instagram', text: 'Instagram', icon: faInstagram },
  youtube: { value: 'youtube', text: 'YouTube', icon: faYoutube },
  tiktok: { value: 'tiktok', text: 'TikTok', icon: faTiktok },
  facebook: { value: 'facebook', text: 'Facebook', icon: faFacebook },
  website: { value: 'website', text: 'Website', icon: faGlobe },
  other: { value: 'other', text: 'Other', icon: faArrowUpRightFromSquare },
} as const;
type SnsTypeType = keyof typeof SnsType;
